import styled, { keyframes } from "styled-components";
import image from "../../../../../assets/img/contactless.jpg"; // swap to bills image
import { blueColor, goldColor } from "../../../../styled/Theme";
import {
  CheckCircleFilled,
  MobileOutlined,
  AppstoreOutlined,
  UserOutlined,
  CloudDownloadOutlined,
  CreditCardOutlined,
  SafetyOutlined,
  BellFilled,
  FileTextOutlined,
  SyncOutlined,
  StopOutlined,
  AuditOutlined,
} from "@ant-design/icons";

const steps = [
  {
    icon: <MobileOutlined />,
    label: "Open OnePayX app or USSD menu",
    sub: "Available on mobile, web & USSD *123#",
  },
  {
    icon: <AppstoreOutlined />,
    label: "Select bill category & provider",
    sub: "Utilities, water, school fees, levies & more",
  },
  {
    icon: <UserOutlined />,
    label: "Enter your account number",
    sub: "Customer or account reference",
  },
  {
    icon: <CloudDownloadOutlined />,
    label: "Bill amount fetched in real time",
    sub: "Pulled directly from the service provider",
  },
  {
    icon: <CreditCardOutlined />,
    label: "Confirm & choose payment source",
    sub: "Wallet, mobile money, or bank",
  },
  {
    icon: <SafetyOutlined />,
    label: "Authorise with PIN or biometrics",
    sub: "256-bit encrypted & instant",
  },
];

const benefits = [
  { icon: <BellFilled />, text: "Instant confirmation via in-app & SMS" },
  { icon: <FileTextOutlined />, text: "Digital receipt in your history" },
  { icon: <SyncOutlined />, text: "Auto-reconciliation with provider" },
  { icon: <StopOutlined />, text: "No queues or manual forms" },
  { icon: <AuditOutlined />, text: "Accurate amounts from providers" },
  { icon: <CheckCircleFilled />, text: "Transparent, traceable payments" },
];

export default function PayBills() {
  return (
    <Wrapper>
      {/* ── Full-bleed left image panel ── */}
      <ImagePanel>
        <PanelImage src={image} alt="Paying bills with OnePayX" />
        <PanelGradient />

        {/* Floating receipt card */}
        <ReceiptCard
          data-aos="fade-up"
          data-aos-duration="700"
          data-aos-delay="400"
        >
          <ReceiptHeader>
            <ReceiptDot $color="#22c55e" />
            <ReceiptTitle>Payment Confirmed</ReceiptTitle>
            <ReceiptTime>Just now</ReceiptTime>
          </ReceiptHeader>
          <ReceiptDivider />
          <ReceiptRow>
            <ReceiptRowLabel>Electricity – UMEME</ReceiptRowLabel>
            <ReceiptRowValue>UGX 85,000</ReceiptRowValue>
          </ReceiptRow>
          <ReceiptRow>
            <ReceiptRowLabel>Reference</ReceiptRowLabel>
            <ReceiptRowValue $mono>#OPX-29471</ReceiptRowValue>
          </ReceiptRow>
          <ReceiptRow>
            <ReceiptRowLabel>Status</ReceiptRowLabel>
            <ReceiptStatus>
              <StatusDot />
              Settled
            </ReceiptStatus>
          </ReceiptRow>
        </ReceiptCard>

        {/* Eyebrow pill */}
        <PanelEyebrow
          data-aos="fade-right"
          data-aos-duration="600"
          data-aos-delay="200"
        >
          <EyebrowDot />
          Pay Bills
        </PanelEyebrow>
      </ImagePanel>

      {/* ── Right content panel ── */}
      <ContentPanel>
        <ContentInner>
          <Headline
            data-aos="fade-up"
            data-aos-duration="700"
            data-aos-delay="100"
          >
            Pay Bills <GoldWord>Easily</GoldWord>
          </Headline>
          <Subline
            data-aos="fade-up"
            data-aos-duration="600"
            data-aos-delay="160"
          >
            Settle utility and institutional bills instantly using OnePayX.
            Transparent, secure, and traceable payments.
          </Subline>

          {/* Steps timeline */}
          <SectionLabel
            data-aos="fade-up"
            data-aos-delay="220"
            data-aos-duration="500"
          >
            How It Works
          </SectionLabel>

          <StepsList>
            {steps.map((s, i) => (
              <StepItem
                key={i}
                data-aos="fade-left"
                data-aos-delay={280 + i * 80}
                data-aos-duration="550"
              >
                <StepLeft>
                  <StepCircle>{i + 1}</StepCircle>
                  {i < steps.length - 1 && <StepLine />}
                </StepLeft>
                <StepRight>
                  <StepIconChip>{s.icon}</StepIconChip>
                  <StepText>
                    <StepLabel>{s.label}</StepLabel>
                    <StepSub>{s.sub}</StepSub>
                  </StepText>
                </StepRight>
              </StepItem>
            ))}
          </StepsList>

          {/* Benefits */}
          <SectionLabel
            data-aos="fade-up"
            data-aos-delay="200"
            data-aos-duration="500"
            style={{ marginTop: "28px" }}
          >
            Why It Matters
          </SectionLabel>

          <BenefitsGrid
            data-aos="fade-up"
            data-aos-delay="260"
            data-aos-duration="600"
          >
            {benefits.map((b, i) => (
              <BenefitChip key={i}>
                <BenefitIcon>{b.icon}</BenefitIcon>
                {b.text}
              </BenefitChip>
            ))}
          </BenefitsGrid>
        </ContentInner>
      </ContentPanel>
    </Wrapper>
  );
}

const pulseGreen = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(34,197,94,0.45); }
  50%       { box-shadow: 0 0 0 6px rgba(34,197,94,0); }
`;

/* ── Styles ── */

const Wrapper = styled.section`
  display: grid;
  grid-template-columns: 1fr 1fr;
  min-height: 680px;
  overflow: hidden;
  background: #fff;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

/* ── Image panel ── */
const ImagePanel = styled.div`
  position: relative;
  overflow: hidden;
  min-height: 520px;

  @media (max-width: 900px) {
    min-height: 320px;
  }
`;

const PanelImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  position: absolute;
  inset: 0;
  transition: transform 0.8s ease;

  ${ImagePanel}:hover & {
    transform: scale(1.03);
  }
`;

const PanelGradient = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    135deg,
    rgba(7, 20, 55, 0.72) 0%,
    rgba(7, 20, 55, 0.25) 60%,
    transparent 100%
  );
  z-index: 1;
`;

const PanelEyebrow = styled.div`
  position: absolute;
  top: 28px;
  left: 28px;
  z-index: 3;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: ${goldColor};
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid ${goldColor}50;
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 5px 14px;
`;

const EyebrowDot = styled.span`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: ${goldColor};
`;

/* Receipt card */
const ReceiptCard = styled.div`
  position: absolute;
  bottom: 32px;
  left: 28px;
  right: 28px;
  z-index: 3;
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(16px);
  border-radius: 18px;
  padding: 18px 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.22);
  border: 1px solid rgba(255, 255, 255, 0.8);
`;

const ReceiptHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
`;

const ReceiptDot = styled.div<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(p) => p.$color};
  animation: ${pulseGreen} 2s ease-in-out infinite;
  flex-shrink: 0;
`;

const ReceiptTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  flex: 1;
`;

const ReceiptTime = styled.div`
  font-size: 11px;
  color: #94a3b8;
  font-weight: 500;
`;

const ReceiptDivider = styled.div`
  height: 1px;
  background: #f1f5f9;
  margin-bottom: 12px;
`;

const ReceiptRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ReceiptRowLabel = styled.div`
  font-size: 12.5px;
  color: #64748b;
`;

const ReceiptRowValue = styled.div<{ $mono?: boolean }>`
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
  font-family: ${(p) => (p.$mono ? "monospace" : "inherit")};
`;

const ReceiptStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12.5px;
  font-weight: 700;
  color: #22c55e;
`;

const StatusDot = styled.div`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #22c55e;
`;

/* ── Content panel ── */
const ContentPanel = styled.div`
  background: #f8fafc;
  display: flex;
  align-items: center;
  border-left: 1px solid #e8f0f8;

  @media (max-width: 900px) {
    border-left: none;
    border-top: 1px solid #e8f0f8;
  }
`;

const ContentInner = styled.div`
  padding: 52px 44px;
  width: 100%;

  @media (max-width: 600px) {
    padding: 40px 24px;
  }
`;

const Headline = styled.h2`
  font-size: 34px;
  font-weight: 900;
  color: #0a1628;
  letter-spacing: -1.2px;
  margin: 0 0 10px;
  line-height: 1.15;
  padding-top: 20px;

  @media (max-width: 768px) {
    font-size: 26px;
  }
`;

const GoldWord = styled.span`
  color: ${goldColor};
`;

const Subline = styled.p`
  font-size: 14.5px;
  color: #64748b;
  line-height: 1.65;
  margin: 0 0 28px;
`;

const SectionLabel = styled.div`
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 1.3px;
  text-transform: uppercase;
  color: ${blueColor};
  margin-bottom: 16px;
`;

/* Steps */
const StepsList = styled.div`
  display: flex;
  flex-direction: column;
`;

const StepItem = styled.div`
  display: flex;
  gap: 0;
  align-items: flex-start;
`;

const StepLeft = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 32px;
  flex-shrink: 0;
`;

const StepCircle = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: ${blueColor};
  color: #fff;
  font-size: 11px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 3px 10px ${blueColor}40;
`;

const StepLine = styled.div`
  width: 1.5px;
  flex: 1;
  min-height: 18px;
  background: linear-gradient(to bottom, ${blueColor}50, ${blueColor}10);
  margin: 3px 0;
`;

const StepRight = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 0 0 16px 12px;
  flex: 1;
`;

const StepIconChip = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: ${blueColor}10;
  color: ${blueColor};
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: -2px;
`;

const StepText = styled.div``;

const StepLabel = styled.div`
  font-size: 13.5px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.3;
`;

const StepSub = styled.div`
  font-size: 12px;
  color: #94a3b8;
  margin-top: 2px;
`;

/* Benefits */
const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const BenefitChip = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 10px 13px;
  font-size: 12.5px;
  font-weight: 600;
  color: #334155;
  transition: all 0.22s ease;
  cursor: default;

  &:hover {
    border-color: ${blueColor}40;
    background: ${blueColor}05;
    color: ${blueColor};
    transform: translateY(-2px);
    box-shadow: 0 4px 14px ${blueColor}10;
  }
`;

const BenefitIcon = styled.span`
  color: ${blueColor};
  font-size: 14px;
  flex-shrink: 0;
`;
